// ---------------------------------------------------------------------------
// usePlanogramSelection
//
// React glue around PlanogramSelection. Replaces the raw
// `useState<Set<string>>(new Set())` + `isSelectionMode` boolean that lived
// directly in PlanogramDetails, plus the duplicated isAllSelected/
// handleSelectAll logic that existed separately in PlanogramVisual's
// row/column header checkboxes and PlanogramTabular's own isAllSelected.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';
import { PlanogramSelection } from '../domain/PlanogramSelection';
import { CellKey, PlanogramGrid } from '../domain/types';

export function usePlanogramSelection() {
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selection, setSelection] = useState<PlanogramSelection>(PlanogramSelection.empty());

    const enterSelectionMode = useCallback(() => {
        setIsSelectionMode(true);
        setSelection(PlanogramSelection.empty());
    }, []);

    const exitSelectionMode = useCallback(() => {
        setIsSelectionMode(false);
        setSelection(PlanogramSelection.empty());
    }, []);

    const toggleSelectionMode = useCallback(() => {
        setIsSelectionMode(prev => !prev);
        setSelection(PlanogramSelection.empty());
    }, []);

    const toggleCell = useCallback((rowIndex: number, colIndex: number) => {
        setSelection(prev => prev.toggle(`${rowIndex}-${colIndex}`));
    }, []);

    const setMany = useCallback((keys: CellKey[], shouldSelect: boolean) => {
        setSelection(prev => prev.setMany(keys, shouldSelect));
    }, []);

    /**
     * Replaces the selection's contents outright (rather than unioning, as
     * setMany does) — needed for GH #555's Shift+Arrow keyboard extension,
     * where each keypress recomputes the WHOLE anchor->focus rectangle from
     * scratch. setMany(keys, true) can only ever grow the set; it has no
     * way to drop cells that fall outside a now-smaller rectangle (e.g.
     * Shift+Down then Shift+Up should shrink back to the single starting
     * cell, not leave the first row's cells stuck selected).
     */
    const replace = useCallback((keys: CellKey[]) => {
        setSelection(PlanogramSelection.of(keys));
    }, []);

    /** Enters selection mode AND sets its initial contents in one state transition — avoids a frame where selection mode is on but the selection is still the empty set enterSelectionMode resets it to. */
    const enterSelectionModeWith = useCallback((keys: CellKey[]) => {
        setIsSelectionMode(true);
        setSelection(PlanogramSelection.of(keys));
    }, []);

    const clear = useCallback(() => setSelection(PlanogramSelection.empty()), []);

    /** Select every visible cell across the whole grid, or one row if rowIndex is given (-1 = all rows). */
    const selectAll = useCallback((grid: PlanogramGrid, rows: number, columns: number, rowIndex = -1) => {
        setSelection(PlanogramSelection.of(PlanogramSelection.visibleKeysFor(grid, rows, columns, rowIndex)));
    }, []);

    return {
        isSelectionMode,
        selection,
        selectedCells: selection.toSet(), // back-compat shape for components still expecting a raw Set
        enterSelectionMode,
        enterSelectionModeWith,
        exitSelectionMode,
        toggleSelectionMode,
        toggleCell,
        setMany,
        replace,
        clear,
        selectAll,
    };
}

export type UsePlanogramSelectionReturn = ReturnType<typeof usePlanogramSelection>;